import { memo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FormattedDate, FormattedMessage } from "react-intl";
import { Amount } from "app/components/ui/Amount/Amount";
import { computeActivityTotal } from "app/utils/utils";
import "./QuotationActivityDate.scss";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ReactSelectField } from "app/components/form/ReactSelectField/ReactSelectField";
import { useFormikContext } from "formik";

const QuotationActivityDate = ({
	availableDates,
	onChange,
	activityCode,
	guestsPerDay,
	hasIncludedGuest,
	index,
}) => {
	const { track } = useAnalytics();

	const { values, errors } = useFormikContext();
	const currentActivity = values?.activities?.find(act => act.code === activityCode);

	const dates = availableDates.map(availableDate => {
		const numberOfParticipants = currentActivity?.guests;

		const guests = guestsPerDay[availableDate];

		const pricingPerParticipant = (guests || []).reduce((acc, { code, pricePerQuantity }) => {
			acc[code] = Object.fromEntries(
				Object.entries(pricePerQuantity).map(([quantity, { price = 0, discount = 0 }]) => [
					quantity,
					{ price, discount },
				])
			);
			return acc;
		}, {});

		const total = computeActivityTotal(pricingPerParticipant, numberOfParticipants);

		return {
			code: availableDate,
			label: (
				<>
					<FormattedDate
						timeZone="UTC"
						month="short"
						day="numeric"
						weekday="short"
						value={availableDate}
					/>
					<Amount value={total} />
				</>
			),
		};
	});

	const activityDate = currentActivity?.date;
	const hasMultipleDates = dates.length > 1;
	const activityLabelClassName = classNames("quotation-activity-date", {
		"quotation-activity-date--single-date": !hasMultipleDates,
	});

	const isDateInvalid = Boolean(errors?.duplicateDates?.includes(activityDate));

	useEffect(() => {
		onChange(activityDate);
	}, [activityDate]);

	const handleDateChange = useCallback(({ code }) => {
		track("activity_date_select");
		onChange(code);
	}, []);

	return (
		<div className={activityLabelClassName}>
			<div className="quotation-activity-date__label">
				{!hasIncludedGuest && (
					<>
						<FormattedMessage
							id={
								hasMultipleDates
									? "quotation.activity.date.label"
									: "quotation.activity.single.date.label"
							}
						/>
						{hasMultipleDates && (
							<div className="quotation-activity-date__label-maxday">
								<FormattedMessage id="quotation.activity.date.label.max" />
							</div>
						)}
					</>
				)}
			</div>

			{hasMultipleDates ? (
				<div className="quotation-activity-date__select">
					<ReactSelectField
						id={`activities[${index}].date`}
						name={`activities[${index}].date`}
						data-testid="activity-date-select-input"
						value={{
							code: activityDate,
							label: (
								<FormattedDate
									timeZone="UTC"
									month="short"
									day="numeric"
									weekday="short"
									value={activityDate}
								/>
							),
						}}
						invalid={isDateInvalid}
						isSearchable={false}
						openMenuOnFocus
						getOptionLabel={({ label }) => label}
						getOptionValue={({ code }) => code}
						blurInputOnSelect={true}
						options={dates}
						onChange={handleDateChange}
						menuPortalMargin={-20}
						menuWidth="100%"
					/>
				</div>
			) : (
				<div className="quotation-activity-date__date">
					<FormattedDate
						timeZone="UTC"
						month="short"
						day="numeric"
						weekday="short"
						value={activityDate}
					/>
				</div>
			)}
		</div>
	);
};

QuotationActivityDate.propTypes = {
	guestsPerDay: PropTypes.object,
	availableDates: PropTypes.array,
	activityCode: PropTypes.string,
	onChange: PropTypes.func,
	hasIncludedGuest: PropTypes.bool,
	index: PropTypes.number,
};

QuotationActivityDate.defaultProps = {
	guestsPerDay: {},
	availableDates: [],
	onChange: () => {},
};

export default memo(QuotationActivityDate);
